import { Fragment, useEffect, useRef, useState } from 'react';
import { hasStudentOutbox } from '../game/systems/StudentProgressOutbox';
import { activateStudentProgress, flushStudentProgress, getStudentProgressStatus, discardStudentProgressConflicts, discardStudentProgressObsolete } from '../game/systems/ProgressData';

// Mount gameplay only after an authoritative load; never upload an empty fallback.
export default function StudentProgressGate({ student, requestFn, onExit, children }) {
  const studentId = student.student_id;
  const exiting = useRef(false);
  const loadEpoch = useRef(0);
  const studentRef = useRef(student);
  studentRef.current = student;
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState({ pending: 0 });
  useEffect(() => {
    let current = true, deactivate;
    loadEpoch.current += 1;
    setLoaded(null); setError('');
    requestFn('/student/progress').then(async response => {
      if (!current || exiting.current) return;
      deactivate = activateStudentProgress(studentRef.current, response, requestFn);
      // Replay only after an authenticated load, then hydrate before game mount.
      try { await flushStudentProgress({ hydrate: true }); } catch { /* Remains durable and visible. */ }
      if (current && !exiting.current) { setStatus(getStudentProgressStatus()); setLoaded(studentId); }
    }).catch(() => { if (current) setError('No pudimos cargar tu progreso. Vuelve a intentar.'); });
    return () => { current = false; loadEpoch.current += 1; deactivate?.(); };
  }, [studentId, requestFn, retry]); // Alias never decides the stored identity.
  useEffect(() => {
    const update = event => { if (event.detail.studentId === studentId) setStatus(event.detail); };
    window.addEventListener('tmag:progress-status', update);
    return () => window.removeEventListener('tmag:progress-status', update);
  }, [studentId]);
  useEffect(() => {
    const warn = event => { if (status.pending) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [status.pending]);
  async function saveBeforeExit() {
    const epoch = loadEpoch.current;
    try {
      await flushStudentProgress();
      // A delayed A save must not log out B after this gate was replaced.
      if (loadEpoch.current === epoch && getStudentProgressStatus().studentId === studentId) await onExit();
    }
    catch { setStatus(s => ({ ...s, error: true })); }
  }
  async function exitBeforeLoad() {
    try {
      if (hasStudentOutbox(studentId)) {
        setError('Primero debemos recuperar tu progreso pendiente. Reintenta antes de salir.');
        return;
      }
    } catch {
      setError('No pudimos revisar tu progreso pendiente. Reintenta para recuperar tu progreso antes de salir.');
      return;
    }
    exiting.current = true;
    try {
      if (await onExit() === false) { exiting.current = false; setError("No pudimos salir del perfil. Vuelve a intentar."); }
    }
    catch { exiting.current = false; setError('No pudimos salir del perfil. Vuelve a intentar.'); }
  }
  if (loaded !== studentId) return <main className="tmag-child-page"><section className="tmag-child-panel">
    <p role={error ? 'alert' : 'status'}>{error || 'Recuperando tu progreso…'}</p>
    {error && <button onClick={() => setRetry(n => n + 1)}>Reintentar</button>}
    <button onClick={exitBeforeLoad}>Salir del perfil</button>
  </section></main>;
  async function discardConflict() {
    if (window.confirm('El servidor rechazó este intento. ¿Descartar el pendiente en conflicto? No cambia tu progreso ya guardado.')) {
      try { await discardStudentProgressConflicts(); } catch { /* Blocked recovery offers retry. */ }
    }
  }
  function discardOld() {
    if (window.confirm('Estos intentos pertenecen a una sesión anterior y no pueden reenviarse con tu sesión actual. ¿Descartarlos de este dispositivo? No cambia el progreso guardado.')) {
      try { discardStudentProgressObsolete(); } catch { /* Storage failure stays blocked. */ }
    }
  }
  return <>
    {(status.pending > 0 || status.blocked) && <p className="tmag-child-notice" role={status.error ? 'alert' : 'status'}>
      {status.review ? 'Hay resultados pendientes que necesitan revisión. Se conservan en este dispositivo; no los descartaremos ni crearemos autorizaciones nuevas. Pide ayuda a un adulto.' : status.conflict ? 'Un intento necesita revisión. No lo enviaremos de nuevo automáticamente. Pide ayuda a un adulto.' : status.recovering ? 'Recuperando tu progreso guardado. Reintenta antes de continuar.' : status.blocked ? 'No hay espacio disponible para guardar otro intento. Resuelve los pendientes antes de continuar.' : status.error ? 'Tu progreso aún no se guardó. Reintenta antes de salir.' : 'Guardando tu progreso…'}
      {(status.error || status.blocked) && !status.conflict && (!status.review || status.pending > status.review) && <button onClick={() => flushStudentProgress().catch(() => {})}>Reintentar guardado</button>}
      {status.blocked && <button onClick={saveBeforeExit}>Salir del perfil</button>}
      {!status.commercial && status.conflict && <button onClick={discardConflict}>Descartar intento en conflicto</button>}
    </p>}
    {!status.commercial && status.obsolete > 0 && <p className="tmag-child-notice" role="status">Hay intentos de una sesión anterior de tu perfil que no podemos reenviar con esta sesión. Pide ayuda a un adulto. <button onClick={discardOld}>Descartar pendientes anteriores</button></p>}
    <div inert={!!status.blocked}>{!status.recovering && <Fragment key={status.generation || 0}>{children(saveBeforeExit)}</Fragment>}</div>
  </>;
}
